import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../api';
import FahrzeugForm from '../components/FahrzeugForm';
import RadsatzForm from '../components/RadsatzForm';
import KmVerlauf from '../components/KmVerlauf';
import EreignisseSection from '../components/EreignisseSection';
import DokumenteSection from '../components/DokumenteSection';
import PersonPicker from '../components/PersonPicker';
import { exportFahrzeugDetail } from '../utils/pdfExport';

function formatDate(d) {
  if (!d) return null;
  const [y, m, day] = d.split('-');
  return `${day}.${m}.${y}`;
}

function getDueBadge(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  const d = new Date(dateStr);
  const diff = Math.ceil((d - today) / 86400000);
  if (diff < 0) return <span className="badge badge-danger" style={{ marginLeft: 8 }}>Überfällig ({Math.abs(diff)}d)</span>;
  if (diff <= 7) return <span className="badge badge-danger" style={{ marginLeft: 8 }}>In {diff} Tag(en)</span>;
  if (diff <= 30) return <span className="badge badge-warning" style={{ marginLeft: 8 }}>In {diff} Tagen</span>;
  return null;
}

function addMonths(dateStr, months) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

function CardHeader({ title, action }) {
  return (
    <div style={{
      padding: '14px 20px',
      borderBottom: '1px solid var(--gray-200)',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
    }}>
      <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--gray-600)' }}>{title}</span>
      {action}
    </div>
  );
}

function Row({ label, value, badge, action }) {
  return (
    <div className="detail-item">
      <div className="detail-key">{label}</div>
      <div className={`detail-val${!value ? ' empty' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span>{value || '–'}</span>
        {badge}
        {action}
      </div>
    </div>
  );
}

// ─── ErledigenModal ───────────────────────────────────────────────────────────

function ErledigenModal({ typ, fahrzeug, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const intervall = typ === 'HU' ? (fahrzeug.hu_intervall_monate || 24)
    : typ === 'SP' ? (fahrzeug.sp_intervall_monate || 6)
    : typ === 'Inspektion' ? (fahrzeug.inspektion_intervall_monate || 12)
    : typ === 'Versicherung' ? (fahrzeug.versicherung_intervall_monate || 12) : 12;

  const [datum, setDatum] = useState(today);
  const [naechstesDatum, setNaechstesDatum] = useState(() => addMonths(today, intervall));
  const [naechsteKm, setNaechsteKm] = useState('');
  const [notiz, setNotiz] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Recalculate naechstesDatum when datum changes
  const handleDatumChange = (val) => {
    setDatum(val);
    setNaechstesDatum(addMonths(val, intervall));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await api.erledigenFaelligkeit(fahrzeug.id, {
        typ, datum, naechstes_datum: naechstesDatum || undefined,
        naechste_km: naechsteKm ? parseInt(naechsteKm) : undefined,
        notiz: notiz || undefined
      });
      onDone();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{typ} erledigen</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group form-full">
                <label className="form-label">Erledigungsdatum</label>
                <input className="form-input" type="date" value={datum} onChange={e => handleDatumChange(e.target.value)} required />
              </div>
              <div className="form-group form-full">
                <label className="form-label">Nächstes Fälligkeitsdatum</label>
                <input className="form-input" type="date" value={naechstesDatum} onChange={e => setNaechstesDatum(e.target.value)} />
                <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>
                  Automatisch berechnet: {datum} + {intervall} Monate. Kann manuell angepasst werden.
                </div>
              </div>
              {typ === 'Inspektion' && (
                <div className="form-group form-full">
                  <label className="form-label">Nächste Inspektion bei km (optional)</label>
                  <input className="form-input" type="number" min="0" value={naechsteKm} onChange={e => setNaechsteKm(e.target.value)} placeholder="z.B. 80000" />
                </div>
              )}
              <div className="form-group form-full">
                <label className="form-label">Notiz (optional)</label>
                <input className="form-input" value={notiz} onChange={e => setNotiz(e.target.value)} placeholder="z.B. Prüfstelle, Kosten…" />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Speichern…' : '✓ Erledigen & Termin setzen'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Radsätze ────────────────────────────────────────────────────────────────

function RadsaetzeSection({ fahrzeugId }) {
  const [radsaetze, setRadsaetze] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editRadsatz, setEditRadsatz] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.getRadsaetze(fahrzeugId).then(setRadsaetze).catch(console.error).finally(() => setLoading(false));
  }, [fahrzeugId]);

  useEffect(load, [load]);

  const handleSave = async (data) => {
    if (editRadsatz) {
      await api.updateRadsatz(editRadsatz.id, data);
    } else {
      await api.createRadsatz(fahrzeugId, data);
    }
    setShowForm(false);
    setEditRadsatz(null);
    load();
  };

  const handleDelete = async () => {
    await api.deleteRadsatz(deleteId);
    setDeleteId(null);
    load();
  };

  const reifenartIcon = (art) => {
    if (!art) return '🔵';
    if (art.includes('Sommer')) return '☀️';
    if (art.includes('Winter')) return '❄️';
    return '🔄';
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <CardHeader
        title="Radsätze"
        action={
          <button className="btn btn-primary btn-sm" onClick={() => { setEditRadsatz(null); setShowForm(true); }}>
            + Radsatz hinzufügen
          </button>
        }
      />

      {loading ? (
        <div style={{ padding: 20, color: 'var(--gray-400)', fontSize: 13 }}>Lade…</div>
      ) : radsaetze.length === 0 ? (
        <div style={{ padding: '24px 20px', color: 'var(--gray-400)', fontSize: 13, textAlign: 'center' }}>
          Noch keine Radsätze angelegt.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Art</th>
                <th style={thStyle}>Marke</th>
                <th style={thStyle}>Größe</th>
                <th style={thStyle}>Profiltiefe</th>
                <th style={thStyle}>Zustand</th>
                <th style={thStyle}>Lagerort</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Notizen</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {radsaetze.map(r => (
                <tr key={r.id} style={r.montiert ? { background: 'var(--success-light)' } : {}}>
                  <td style={tdStyle}>
                    <span style={{ marginRight: 6 }}>{reifenartIcon(r.reifenart)}</span>
                    {r.reifenart || '–'}
                  </td>
                  <td style={tdStyle}>{r.reifenmarke || '–'}</td>
                  <td style={tdStyle}>{r.reifengroesse || '–'}</td>
                  <td style={tdStyle}>{r.profiltiefe || '–'}</td>
                  <td style={tdStyle}>
                    {r.zustand ? (
                      <span className={`badge ${
                        r.zustand.includes('Sehr gut') ? 'badge-success' :
                        r.zustand.includes('Gut') ? 'badge-success' :
                        r.zustand.includes('Mittel') ? 'badge-warning' : 'badge-danger'
                      }`}>{r.zustand}</span>
                    ) : '–'}
                  </td>
                  <td style={tdStyle}>
                    {r.fremdeingelagert ? (
                      <div>
                        <span className="badge badge-warning" style={{ marginBottom: 4, display: 'inline-block' }}>Fremdlager</span>
                        {r.lagerort_name && (
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 12 }}>{r.lagerort_name}</div>
                            {r.lagerort_telefon && <a href={`tel:${r.lagerort_telefon}`} style={{ display: 'block', fontSize: 11, color: 'var(--primary)' }}>📞 {r.lagerort_telefon}</a>}
                            {r.lagerort_email && <a href={`mailto:${r.lagerort_email}`} style={{ display: 'block', fontSize: 11, color: 'var(--primary)' }}>✉️ {r.lagerort_email}</a>}
                          </div>
                        )}
                        {!r.lagerort_name && (r.einlagerungsort || '–')}
                      </div>
                    ) : (r.einlagerungsort || '–')}
                  </td>
                  <td style={tdStyle}>
                    {r.montiert
                      ? <span className="badge badge-success">✓ Montiert</span>
                      : <span className="badge badge-gray">Eingelagert</span>
                    }
                  </td>
                  <td style={tdStyle}>{r.notizen || '–'}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => { setEditRadsatz(r); setShowForm(true); }}>✏️</button>
                      <button className="btn btn-danger btn-sm" onClick={() => setDeleteId(r.id)}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <RadsatzForm
          initial={editRadsatz}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditRadsatz(null); }}
        />
      )}

      {deleteId && (
        <div className="modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Radsatz löschen</span>
              <button className="modal-close" onClick={() => setDeleteId(null)}>✕</button>
            </div>
            <div className="modal-body confirm-dialog">
              <p>Diesen Radsatz wirklich löschen?</p>
              <div className="confirm-actions">
                <button className="btn btn-secondary" onClick={() => setDeleteId(null)}>Abbrechen</button>
                <button className="btn btn-danger" onClick={handleDelete}>Löschen</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Fahrer-Sektion ───────────────────────────────────────────────────────────

function FahrerSection({ fahrzeugId, kontaktbuchId }) {
  const [fahrer, setFahrer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.getFahrer(fahrzeugId)
      .then(setFahrer).catch(console.error).finally(() => setLoading(false));
  }, [fahrzeugId]);

  useEffect(load, [load]);

  const handlePersonSelected = async (person) => {
    setShowPicker(false);
    try {
      await api.addFahrer(fahrzeugId, { ansprechpartner_id: person.id });
      load();
    } catch (err) {
      alert('Fehler: ' + err.message);
    }
  };

  const handleRemove = async (apId) => {
    setRemoving(apId);
    try {
      await api.removeFahrer(fahrzeugId, apId);
      load();
    } catch (err) {
      alert('Fehler: ' + err.message);
    } finally {
      setRemoving(null);
    }
  };

  const excludeIds = fahrer.map(f => f.ansprechpartner_id);

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <CardHeader
        title="Ansprechpartner"
        action={
          <button className="btn btn-primary btn-sm" onClick={() => setShowPicker(true)}>
            + Ansprechpartner zuordnen
          </button>
        }
      />
      {loading ? (
        <div style={{ padding: 20, color: 'var(--gray-400)', fontSize: 13 }}>Lade…</div>
      ) : fahrer.length === 0 ? (
        <div style={{ padding: '24px 20px', color: 'var(--gray-400)', fontSize: 13, textAlign: 'center' }}>
          Noch keine Ansprechpartner zugeordnet.
        </div>
      ) : (
        <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
          {fahrer.map(f => (
            <div key={f.ansprechpartner_id} style={{
              padding: '12px 14px', border: '1px solid var(--gray-200)',
              borderRadius: 8, background: 'var(--gray-50)', position: 'relative'
            }}>
              <button
                onClick={() => handleRemove(f.ansprechpartner_id)}
                disabled={removing === f.ansprechpartner_id}
                style={{
                  position: 'absolute', top: 8, right: 8,
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--gray-300)', fontSize: 14, lineHeight: 1, padding: 2,
                  borderRadius: 4, transition: 'color .15s'
                }}
                onMouseEnter={e => e.target.style.color = 'var(--danger)'}
                onMouseLeave={e => e.target.style.color = 'var(--gray-300)'}
                title="Fahrer entfernen"
              >✕</button>
              {f.funktion && (
                <div style={{ marginBottom: 6 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--primary)', background: 'var(--primary-light)', borderRadius: 4, padding: '2px 6px' }}>{f.funktion}</span>
                </div>
              )}
              {f.kontakt_name && (
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 3 }}>{f.kontakt_name}</div>
              )}
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--gray-800)', marginBottom: 6, paddingRight: 20 }}>{f.name}</div>
              {f.telefon && <a href={`tel:${f.telefon}`} style={{ display: 'block', fontSize: 12, color: 'var(--primary)', fontWeight: 500 }}>{f.telefon}</a>}
              {f.mobil && <a href={`tel:${f.mobil}`} style={{ display: 'block', fontSize: 12, color: 'var(--primary)' }}>{f.mobil}</a>}
              {f.email && <a href={`mailto:${f.email}`} style={{ display: 'block', fontSize: 12, color: 'var(--primary)' }}>{f.email}</a>}
            </div>
          ))}
        </div>
      )}

      {showPicker && (
        <PersonPicker
          onSelect={handlePersonSelected}
          onClose={() => setShowPicker(false)}
          excludeIds={excludeIds}
          kontaktId={kontaktbuchId}
        />
      )}
    </div>
  );
}

// ─── Leasing km projection ────────────────────────────────────────────────────

function LeasingKmBar({ fahrzeug }) {
  const { leasing_km_gesamt, leasing_km_start, kilometerstand, vertragsbeginn, vertragsende } = fahrzeug;
  if (!leasing_km_gesamt || !leasing_km_start || !kilometerstand) return null;

  const today = new Date();
  const start = vertragsbeginn ? new Date(vertragsbeginn) : null;
  const end = vertragsende ? new Date(vertragsende) : null;

  const kmDriven = Number(kilometerstand) - Number(leasing_km_start);
  const allowed = Number(leasing_km_gesamt);
  const pct = Math.min(100, Math.round((kmDriven / allowed) * 100));

  let projected = null;
  if (start && end && kmDriven > 0) {
    const elapsed = Math.max(1, (today - start) / 86400000);
    const remaining = Math.max(0, (end - today) / 86400000);
    projected = Math.round(kmDriven + (kmDriven / elapsed) * remaining);
  }

  const overPct = projected !== null && projected > allowed;

  return (
    <div style={{ padding: '0 20px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--gray-600)', marginBottom: 6 }}>
        <span>Gefahren: <strong>{kmDriven.toLocaleString('de-DE')} km</strong> von {allowed.toLocaleString('de-DE')} km</span>
        <span style={{ fontWeight: 600 }}>{pct} %</span>
      </div>
      <div style={{ height: 10, background: 'var(--gray-200)', borderRadius: 5, overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 5, transition: 'width .3s',
          width: `${pct}%`,
          background: pct >= 95 ? 'var(--danger)' : pct >= 80 ? 'var(--warning)' : 'var(--success)'
        }} />
      </div>
      {projected !== null && (
        <div style={{ marginTop: 6, fontSize: 12, color: overPct ? 'var(--danger)' : 'var(--gray-500)' }}>
          Hochrechnung Vertragsende: <strong style={{ color: overPct ? 'var(--danger)' : 'inherit' }}>
            {projected.toLocaleString('de-DE')} km
          </strong>
          {overPct && <span className="badge badge-danger" style={{ marginLeft: 8 }}>Überschreitung erwartet</span>}
        </div>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function FahrzeugDetail({ navigate, fahrzeugId }) {
  const [fahrzeug, setFahrzeug] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [radsaetzeData, setRadsaetzeData] = useState([]);
  const [erledigenModal, setErledigenModal] = useState(null); // typ string

  const load = useCallback(() => {
    setLoading(true);
    api.getFahrzeug(fahrzeugId).then(setFahrzeug).catch(console.error).finally(() => setLoading(false));
  }, [fahrzeugId]);

  useEffect(load, [load]);

  useEffect(() => {
    if (!fahrzeugId) return;
    api.getRadsaetze(fahrzeugId).then(setRadsaetzeData).catch(() => {});
  }, [fahrzeugId, fahrzeug]);

  const handleSave = async (data) => {
    await api.updateFahrzeug(fahrzeugId, { ...data, kunden_id: fahrzeug.kunden_id });
    setShowEdit(false);
    load();
  };

  const ErledigenBtn = ({ typ }) => (
    <button
      className="btn btn-secondary btn-sm"
      style={{ fontSize: 11 }}
      onClick={() => setErledigenModal(typ)}
    >
      ✓ Erledigen
    </button>
  );

  if (loading) return <div className="spinner">Lade Fahrzeugdaten…</div>;
  if (!fahrzeug) return <div className="alert alert-error">Fahrzeug nicht gefunden.</div>;

  return (
    <div>
      <div className="breadcrumb">
        <span className="breadcrumb-link" onClick={() => navigate('kunden')}>Kunden</span>
        <span className="breadcrumb-sep">›</span>
        <span className="breadcrumb-link" onClick={() => navigate('kunde-detail', { kundenId: fahrzeug.kunden_id })}>
          {fahrzeug.kunden_name}
        </span>
        <span className="breadcrumb-sep">›</span>
        <span>{fahrzeug.kennzeichen}</span>
      </div>

      <div className="page-header">
        <div>
          <div className="page-title">{fahrzeug.kennzeichen}</div>
          <div className="page-subtitle">{[fahrzeug.marke, fahrzeug.modell, fahrzeug.farbe].filter(Boolean).join(' · ')}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => exportFahrzeugDetail(fahrzeug, radsaetzeData, [])}>
            📄 PDF Export
          </button>
          <button className="btn btn-primary" onClick={() => setShowEdit(true)}>
            ✏️ Bearbeiten
          </button>
        </div>
      </div>

      {/* Fahrzeugdaten */}
      <div className="card" style={{ marginBottom: 20 }}>
        <CardHeader title="Fahrzeugdaten" />
        <div className="detail-grid">
          <Row label="Fahrzeugtyp" value={fahrzeug.fahrzeugtyp} />
          <Row label="Kennzeichen" value={fahrzeug.kennzeichen} />
          <Row label="Marke" value={fahrzeug.marke} />
          <Row label="Modell" value={fahrzeug.modell} />
          <Row label="Farbe" value={fahrzeug.farbe} />
          <Row label="FIN" value={fahrzeug.fin} />
          <Row label="Erstzulassung" value={formatDate(fahrzeug.erstzulassung)} />
          <Row label="Kraftstoffart" value={fahrzeug.kraftstoffart} />
          <Row
            label="Kilometerstand"
            value={fahrzeug.kilometerstand ? Number(fahrzeug.kilometerstand).toLocaleString('de-DE') + ' km' : null}
          />
        </div>
      </div>

      {/* Prüftermine */}
      <div className="card" style={{ marginBottom: 20 }}>
        <CardHeader title="Prüftermine & Inspektion" />
        <div className="detail-grid">
          <Row
            label="HU-Fälligkeit"
            value={formatDate(fahrzeug.hu_faelligkeit)}
            badge={getDueBadge(fahrzeug.hu_faelligkeit)}
            action={fahrzeug.hu_faelligkeit ? <ErledigenBtn typ="HU" /> : null}
          />
          <Row
            label="SP-Fälligkeit"
            value={formatDate(fahrzeug.sp_faelligkeit)}
            badge={getDueBadge(fahrzeug.sp_faelligkeit)}
            action={fahrzeug.sp_faelligkeit ? <ErledigenBtn typ="SP" /> : null}
          />
          <Row
            label="Nächste Inspektion"
            value={formatDate(fahrzeug.naechste_inspektion_datum)}
            badge={getDueBadge(fahrzeug.naechste_inspektion_datum)}
            action={fahrzeug.naechste_inspektion_datum ? <ErledigenBtn typ="Inspektion" /> : null}
          />
          <Row label="Inspektion bei km" value={fahrzeug.naechste_inspektion_km ? Number(fahrzeug.naechste_inspektion_km).toLocaleString('de-DE') + ' km' : null} />
        </div>
      </div>

      {/* Versicherung */}
      <div className="card" style={{ marginBottom: 20 }}>
        <CardHeader title="Versicherung" />
        <div className="detail-grid">
          <Row label="Gesellschaft" value={fahrzeug.versicherungsgesellschaft} />
          <Row label="Versicherungsnummer" value={fahrzeug.versicherungsnummer} />
          <Row label="Versicherungsart" value={fahrzeug.versicherungsart} />
          <Row
            label="Versicherungsablauf"
            value={formatDate(fahrzeug.versicherungsablauf)}
            badge={getDueBadge(fahrzeug.versicherungsablauf)}
            action={fahrzeug.versicherungsablauf ? <ErledigenBtn typ="Versicherung" /> : null}
          />
        </div>
      </div>

      {/* Finanzierung / Leasing */}
      {fahrzeug.finanzierungsart && (
        <div className="card" style={{ marginBottom: 20 }}>
          <CardHeader title="Finanzierung / Leasing" />
          <div className="detail-grid">
            <Row label="Art" value={fahrzeug.finanzierungsart} />
            {fahrzeug.finanzierungsanbieter && <Row label="Anbieter / Bank" value={fahrzeug.finanzierungsanbieter} />}
            {fahrzeug.monatliche_rate && <Row label="Monatliche Rate" value={`${Number(fahrzeug.monatliche_rate).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €`} />}
            {fahrzeug.vertragsbeginn && <Row label="Vertragsbeginn" value={formatDate(fahrzeug.vertragsbeginn)} />}
            {fahrzeug.vertragsende && (
              <Row
                label="Vertragsende"
                value={formatDate(fahrzeug.vertragsende)}
                badge={getDueBadge(fahrzeug.vertragsende)}
              />
            )}
            {fahrzeug.finanzierungsart === 'Leasing' && fahrzeug.leasing_km_gesamt && (
              <Row label="Laufleistung laut Vertrag" value={`${Number(fahrzeug.leasing_km_gesamt).toLocaleString('de-DE')} km`} />
            )}
            {fahrzeug.finanzierungsart === 'Leasing' && fahrzeug.leasing_km_start && (
              <Row label="km-Stand bei Vertragsbeginn" value={`${Number(fahrzeug.leasing_km_start).toLocaleString('de-DE')} km`} />
            )}
            {fahrzeug.finanzierungsart === 'Leasing' && fahrzeug.leasing_bereifung && (
              <Row label="Bereifung" value={fahrzeug.leasing_bereifung === '8' ? '8-fach (zwei Sätze)' : '4-fach (ein Satz)'} />
            )}
          </div>
          {fahrzeug.finanzierungsart === 'Leasing' && fahrzeug.leasing_bereifung === '8' && (
            <div style={{ margin: '0 20px 16px', padding: '10px 14px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
              ⚠️ <strong>Hinweis:</strong> Bei 8-fach Bereifung muss der zweite Felgensatz am Vertragsende zurückgegeben werden.
            </div>
          )}
          {fahrzeug.finanzierungsart === 'Leasing' && <LeasingKmBar fahrzeug={fahrzeug} />}
        </div>
      )}

      {/* Fahrer */}
      <FahrerSection fahrzeugId={fahrzeugId} kontaktbuchId={fahrzeug.kontaktbuch_id} />

      {/* KM-Verlauf */}
      <KmVerlauf
        fahrzeugId={fahrzeugId}
        currentKm={fahrzeug.kilometerstand}
        onUpdate={load}
      />

      {/* Radsätze */}
      <RadsaetzeSection fahrzeugId={fahrzeugId} />

      {/* Ereignisse / Historie */}
      <EreignisseSection fahrzeugId={fahrzeugId} />

      {/* Dokumente & Fotos */}
      <DokumenteSection fahrzeugId={fahrzeugId} />

      {/* Notizen */}
      {fahrzeug.notizen && (
        <div className="card">
          <CardHeader title="Notizen" />
          <div style={{ padding: 20, whiteSpace: 'pre-wrap', color: 'var(--gray-700)' }}>
            {fahrzeug.notizen}
          </div>
        </div>
      )}

      {showEdit && (
        <FahrzeugForm
          initial={fahrzeug}
          kundenId={fahrzeug.kunden_id}
          onSave={handleSave}
          onClose={() => setShowEdit(false)}
        />
      )}

      {erledigenModal && (
        <ErledigenModal
          typ={erledigenModal}
          fahrzeug={fahrzeug}
          onClose={() => setErledigenModal(null)}
          onDone={() => { setErledigenModal(null); load(); }}
        />
      )}
    </div>
  );
}

const thStyle = {
  textAlign: 'left', padding: '8px 14px',
  fontSize: 11, fontWeight: 600, color: 'var(--gray-500)',
  textTransform: 'uppercase', letterSpacing: '.5px',
  background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)'
};
const tdStyle = {
  padding: '10px 14px',
  borderBottom: '1px solid var(--gray-100)',
  color: 'var(--gray-700)', fontSize: 14
};
